<template>
  <div>
    <!-- Repository Selection for Admin -->
    <b-card
      v-if="user.role === 'admin' || user.role === 'superadmin'"
      class="mb-3"
    >
      <b-card-header>
        <h5 class="mb-0">
          <feather-icon
            icon="MapPinIcon"
            class="mr-1"
          />
          Sélection du dépôt
        </h5>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col
            v-for="(repo, index) in repositories"
            :key="index"
            md="3"
            class="mb-2"
          >
            <b-card
              :class="{ 'border-primary bg-light-primary': isSelected(repo) }"
              class="cursor-pointer text-center h-100"
              @click="selectRepository(repo)"
            >
              <b-card-body class="py-2">
                <feather-icon
                  :icon="isSelected(repo) ? 'CheckCircleIcon' : 'MapPinIcon'"
                  :class="isSelected(repo) ? 'text-primary' : 'text-muted'"
                  size="24"
                />
                <h6
                  class="mt-1 mb-0"
                  :class="isSelected(repo) ? 'text-primary' : ''"
                >
                  {{ repo.name }}
                </h6>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Scanner Section -->
    <b-card
      class="mb-3"
      aria-label="Section de scan des colis"
    >
      <b-card-header>
        <h5 class="mb-0">
          <feather-icon
            icon="ScanIcon"
            class="mr-1"
            aria-hidden="true"
          />
          Scanner de colis
        </h5>
      </b-card-header>
      <b-card-body>
        <b-form-group
          label="Référence du colis à scanner"
          label-for="scanner-input"
          description="Veuillez scanner ou saisir la référence du colis à réceptionner. Appuyez sur Entrée pour valider."
        >
          <span
            id="scanner-instructions"
            class="sr-only"
          >Champ de saisie pour scanner ou entrer la référence d'un colis à réceptionner. Après la saisie, appuyez sur Entrée pour valider.</span>
          <b-input-group class="input-group-merge">
            <b-input-group-prepend is-text>
              <feather-icon
                icon="MaximizeIcon"
                aria-hidden="true"
              />
            </b-input-group-prepend>
            <b-form-input
              id="scanner-input"
              ref="select"
              v-model="ref"
              :aria-describedby="'scanner-instructions'"
              aria-label="Champ de scan ou saisie de la référence du colis"
              placeholder="Scanner ou saisir la référence du colis"
              @change="checkOrder"
            />
          </b-input-group>
        </b-form-group>
      </b-card-body>
    </b-card>

    <!-- Stats Cards -->
    <b-row class="mb-3">
      <b-col md="6">
        <b-card
          class="text-center border-warning"
          aria-label="Statistique des colis à valider"
        >
          <feather-icon
            icon="ClockIcon"
            size="32"
            class="text-warning mb-2"
            aria-hidden="true"
          />
          <h4 class="text-warning mb-0">
            {{ orders.length }}
          </h4>
          <p class="text-muted mb-0">
            Colis à valider
          </p>
        </b-card>
      </b-col>
      <b-col md="6">
        <b-card
          class="text-center border-success"
          aria-label="Statistique des colis validés"
        >
          <feather-icon
            icon="CheckCircleIcon"
            size="32"
            class="text-success mb-2"
            aria-hidden="true"
          />
          <h4 class="text-success mb-0">
            {{ selected.length }}
          </h4>
          <p class="text-muted mb-0">
            Colis validés
          </p>
        </b-card>
      </b-col>
    </b-row>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Chargement..."
      />
      <p class="mt-2">
        Chargement des colis en transfert...
      </p>
    </div>

    <!-- Tables Section -->
    <b-row v-else>
      <!-- Orders to Validate Table -->
      <b-col md="6">
        <b-card aria-label="Tableau des colis à valider">
          <b-card-header class="d-flex justify-content-between align-items-center">
            <h6 class="mb-0">
              <feather-icon
                icon="PackageIcon"
                class="mr-1"
                aria-hidden="true"
              />
              Colis à valider ({{ orders.length }})
            </h6>
            <b-badge
              variant="warning"
              pill
              aria-label="Nombre de colis à valider"
            >
              {{ orders.length }}
            </b-badge>
          </b-card-header>
          <b-card-body class="p-0">
            <span class="sr-only">Tableau listant les colis en attente de validation pour la réception en dépôt. Utilisez le bouton Valider pour chaque colis après vérification.</span>
            <vue-good-table
              :columns="columnsToTransfer"
              :rows="orders"
              :search-options="{ enabled: true, externalQuery: searchTerm }"
              :pagination-options="{ enabled: true, perPage: pageLength }"
              max-height="500px"
              aria-label="Colis à valider"
            >
              <template
                slot="table-row"
                slot-scope="props"
              >
                <span v-if="props.column.field === 'id'">
                  <span
                    class="font-weight-bold"
                    :aria-label="'BL ' + props.row.id"
                  >{{ props.row.id }}</span>
                  <b-badge
                    v-if="props.row.status === 18"
                    variant="info"
                    class="ml-1"
                    aria-label="Statut : Planification"
                  >Planification</b-badge>
                  <br>
                  <span
                    v-if="props.row.is_an_exchange"
                    class="text-danger font-weight-bold"
                    aria-label="Colis d'échange"
                  >
                    Échange
                  </span>
                </span>

                <span v-else-if="props.column.field === 'action'">
                  <b-button
                    variant="success"
                    size="sm"
                    :aria-label="`Valider la réception du colis BL ${props.row.id}`"
                    @click="addItem(props.row)"
                  >
                    <feather-icon
                      icon="CheckIcon"
                      size="14"
                      aria-hidden="true"
                    />
                    <span class="sr-only">Valider la réception du colis</span>
                  </b-button>
                </span>

                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- Validated Orders Table -->
      <b-col md="6">
        <b-card aria-label="Tableau des colis validés">
          <b-card-header class="d-flex justify-content-between align-items-center">
            <h6 class="mb-0">
              <feather-icon
                icon="CheckSquareIcon"
                class="mr-1"
                aria-hidden="true"
              />
              Colis validés ({{ selected.length }})
            </h6>
            <b-badge
              variant="success"
              pill
              aria-label="Nombre de colis validés"
            >
              {{ selected.length }}
            </b-badge>
          </b-card-header>
          <b-card-body class="p-0">
            <span class="sr-only">Tableau listant les colis validés pour la réception en dépôt. Ces colis ont été scannés et validés avec succès.</span>
            <vue-good-table
              :columns="columnsTransfer"
              :rows="selected"
              :search-options="{ enabled: true, externalQuery: searchTerm }"
              :pagination-options="{ enabled: true, perPage: pageLength }"
              max-height="500px"
              aria-label="Colis validés"
            >
              <template
                slot="table-row"
                slot-scope="props"
              >
                <span v-if="props.column.field === 'id'">
                  <span
                    class="font-weight-bold"
                    :aria-label="'BL ' + props.row.id"
                  >{{ props.row.id }}</span>
                  <br>
                  <span
                    v-if="props.row.is_an_exchange"
                    class="text-danger font-weight-bold"
                    aria-label="Colis d'échange"
                  >
                    Échange
                  </span>
                </span>

                <span v-else>
                  {{ props.formattedRow[props.column.field] }}
                </span>
              </template>
            </vue-good-table>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import storeAuth from '@/store/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import {
  BCard, BCardHeader, BCardBody, BFormGroup, BFormInput,
  BInputGroup, BInputGroupPrepend, BSpinner,
  BRow, BCol, BBadge, BButton,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import transfere from '@/assets/audio/transfere.mp3'
import errorVoice from '@/assets/audio/error.mp3'
import alreadyScanned from '@/assets/audio/colis-deja-scanne.mp3'

export default {
  name: 'TransferReception',
  components: {
    VueGoodTable,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BCard,
    BCardHeader,
    BCardBody,
    BSpinner,
    BRow,
    BCol,
    BBadge,
    BButton,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      isLoading: false,
      pageLength: 50,
      columnsToTransfer: [
        {
          label: 'Action',
          field: 'action',
          width: '80px',
        },
        {
          label: 'BL',
          field: 'id',
          width: '100px',
        },
        {
          label: 'Client',
          field: 'name',
        },
        {
          label: 'Fournisseur',
          field: 'supplier.username',
        },
        {
          label: 'Gouvernorat',
          field: 'governorate',
        },
        {
          label: 'Téléphone',
          field: 'phone1',
        },
      ],
      columnsTransfer: [
        {
          label: 'BL',
          field: 'id',
          width: '100px',
        },
        {
          label: 'Client',
          field: 'name',
        },
        {
          label: 'Fournisseur',
          field: 'supplier.username',
        },
        {
          label: 'Gouvernorat',
          field: 'governorate',
        },
        {
          label: 'Téléphone',
          field: 'phone1',
        },
      ],
      orders: [],
      selected: [],
      searchTerm: '',
      ref: '',
      user: {},
      repository: {},
      repositories: [],
      transfere,
      errorVoice,
      alreadyScanned,
    }
  },
  created() {
    this.user = storeAuth.state.user
    if (this.user.role === 'manager') {
      this.getOrdersToTransfer()
    }
    this.getRepositories()
  },
  mounted() {
    if (this.$refs.select) {
      this.$refs.select.focus()
    }
  },
  methods: {
    isSelected(repository) {
      return this.repository && this.repository.id === repository.id
    },
    getRandomColor(index) {
      const colors = ['#FF5733', '#33FF57', '#5733FF', '#FF33D1', '#33D1FF']
      const colorIndex = index % colors.length
      return colors[colorIndex]
    },
    selectRepository(repository) {
      this.repository = repository
      this.getOrdersToTransfer()
    },
    async checkOrder() {
      const existingOrder = this.selected.find(item => item.id === this.ref)

      if (existingOrder) {
        await this.withTitle('Colis déjà scanné sur la liste sélectionnée')
        this.playAudio(this.alreadyScanned)
      } else {
        const searchedItem = this.orders.find(element => element.id === this.ref)

        if (searchedItem) {
          await this.addItem(searchedItem)
          this.ref = ''
          this.playAudio(this.transfere)
        } else {
          this.playAudio(this.errorVoice)
          this.ref = ''
        }
      }
    },
    async playAudio(payload) {
      const voice = new Audio(payload)
      voice.addEventListener('canplaythrough', () => {
        voice.play()
      })
    },
    async delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    async addItem(item) {
      this.selected.push(item)
      const i = this.orders.findIndex(element => element.id === item.id)
      let repository = null
      if (storeAuth.state.user.role === 'admin' || storeAuth.state.user.role === 'superadmin') {
        repository = this.repository.id
      } else {
        repository = storeAuth.state.user.repository
      }
      await axios.post('/api/transfer-vouchers/validate-transfer-orders/', {
        order_id: item.id,
        to_repository: repository,
      })
      this.orders.splice(i, 1)
      this.showToast('success', 'top-center', 'Status modifié avec succés')
    },
    async getRepositories() {
      const { data } = await axios.get('/api/repositories/')
      this.repositories = data
    },
    async withTitle(text) {
      await this.$swal({
        title: 'Alerte',
        icon: 'warning',
        text,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      })
      this.ref = ''
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    async getOrdersToTransfer() {
      this.isLoading = true
      this.selected = []
      let repository = null
      if (storeAuth.state.user.role === 'admin' || storeAuth.state.user.role === 'superadmin') {
        repository = this.repository.id
      } else {
        repository = storeAuth.state.user.repository
      }

      if (!repository) {
        this.isLoading = false
        return
      }

      try {
        const { data } = await axios.get('/api/transfer-vouchers/get-orders-with-is-transfer-false/', {
          params: { to_repository: repository },
        })
        this.orders = data
      } catch (error) {
        this.showToast('danger', 'top-right', 'Erreur lors du chargement des colis')
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-good-table.scss';

.selected-card {
  background-color: rgba(115, 103, 240, 0.12);
  border-color: #7367f0 !important;
}
</style>
